import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { switchMap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from '../services/runtime-config.service';
import { getApiUrl } from '../config/config-helpers';

/**
 * Auth interceptor that adds authentication token to HTTP requests
 * Supports both Entra (MSAL) and Local (email/password) authentication
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const runtimeConfig = inject(RuntimeConfigService);
  
  // Get effective API URL (runtime config or environment fallback)
  const apiUrl = getApiUrl(runtimeConfig);
  
  // SECURITY: Only add auth tokens to requests to our own API
  // This prevents token leakage to third-party services
  let isOurApi = false;
  
  if (req.url.startsWith('/')) {
    // Relative URL - validate it's a relative path to our API
    // Only allow /api/* paths, not other paths like /api-docs, /assets, etc.
    isOurApi = req.url.startsWith('/api/') || req.url === '/api';
  } else if (req.url.startsWith('http://') || req.url.startsWith('https://')) {
    // Absolute URL - validate it's the same origin as our API
    // CRITICAL: Only attach tokens if the origin matches our API origin AND path is our API
    try {
      const requestUrl = new URL(req.url);
      const apiUrlObj = new URL(apiUrl, window.location.origin);
      
      // Check if protocol, hostname, and port all match
      const sameOrigin = requestUrl.origin === apiUrlObj.origin;
      
      // Check if the path is our API path (with same boundary check as relative URLs)
      // Prevent token leakage to paths like /api-docs, /apikey, etc.
      const requestPath = requestUrl.pathname;
      const apiPath = apiUrlObj.pathname;
      const isApiPath = requestPath.startsWith(apiPath + '/') || requestPath === apiPath;
      
      isOurApi = sameOrigin && isApiPath;
    } catch (e) {
      // Invalid URL - don't attach token
      // Log sanitized version (origin only) to help debugging without exposing sensitive data
      try {
        const sanitizedUrl = new URL(req.url).origin;
        console.warn('Invalid URL in auth interceptor - token not attached. Origin:', sanitizedUrl);
      } catch {
        console.warn('Invalid URL in auth interceptor - token not attached (malformed URL)');
      }
      isOurApi = false;
    }
  }
  // For any other URL format, don't attach tokens
  
  if (!isOurApi) {
    return next(req);
  }
  
  // Skip token for public auth endpoints
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/auth/request-password-reset',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/resend-email-verification',
    '/config'
  ];
  // Handle both absolute and relative URLs
  const urlPath = req.url.startsWith('/api') 
    ? req.url.substring('/api'.length)
    : req.url.substring(apiUrl.length);
  if (publicEndpoints.some(endpoint => urlPath.startsWith(endpoint))) {
    return next(req);
  }
  
  // Check if user is authenticated
  if (!authService.isAuthenticated()) {
    return next(req);
  }
  
  // Get token from auth service (works for both Entra and Local)
  return authService.getAccessToken().pipe(
    catchError(error => {
      console.error('Error acquiring token:', error);
      return throwError(() => error);
    }),
    switchMap(token => {
      if (!token) {
        return next(req);
      }
      
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      return next(authReq);
    })
  );
};
